<template>
  <div class="tab-item">
    <div class="col-100 filtre" @keydown.enter="filter">
      <h2>Filtres</h2>
      <div class="labels">
        <SuggesterInput
              v-on:update:suggesterState="suggesterState = $event"
              ressource="cultivations"
              fieldname="name"
              verbose="Culture"
              select-required
              v-model="searchInput.cultivations"
            >
        </SuggesterInput>
        <SuggesterInput
          v-on:update:suggesterState="suggesterState = $event"
          ressource="targets"
          fieldname="name"
          verbose="Cible"
          v-model="searchInput.targets"
        ></SuggesterInput>
        <div class="checkbox">
          <input
            id="usageAutorise"
            type="checkbox"
            value="authorized"
            v-model="searchInput.authorizations"
          />
          <label for="usageAutorise">Usages autorisés</label>
        </div>
        <div v-if="isProductPhyto" class="checkbox">
          <input
            id="usagesRetires"
            type="checkbox"
            value="unauthorized"
            v-model="searchInput.authorizations"
          />
          <label for="usagesRetires">Usages retirés</label>
        </div>
      </div>

      <div class="annuaire-filtre-actions">
        <div class="annuaire-filtre-boutons">
          <button class="grey-background" @click="clearSearch">
            Réinitialiser la recherche
          </button>
          <button class="orange-background" @click="filter">Filtrer</button>
        </div>
      </div>
    </div>

    <div class="col-100 produit-usage">
      <use-accordion-item
        v-for="group in usesGroups"
        :key="group.key"
        :item="group"
        :product="product"
      ></use-accordion-item>
    </div>
  </div>
</template>

<script>
import Product from '@/api/products.js'
import SearchMixin from '@/mixins/SearchMixin.js'
import SuggesterInput from '@/components/SuggesterInput'
import UseAccordionItem from '@/components/UseAccordionItem'
import _groupBy from 'lodash/groupBy'
import _sortBy from 'lodash/sortBy'

export default {
  name: 'ProductUses',
  components: { UseAccordionItem, SuggesterInput },
  mixins: [SearchMixin],

  props: {
    product: Object,
    productId: String
  },

  data () {
    return {
      usesGroups: [],
      searchInput: {
        cultivations: '',
        targets: '',
        authorizations: []
      },
      queryParams: {
        authorizations: []
      },
      suggesterState: false
    }
  },

  beforeRouteEnter (to, from, next) {
    next((vm) => {
      vm.queryParams = { ...to.query }
      vm.handleMultiValuatedQueryParam(['authorizations'])
      vm.init()
    })
  },

  beforeRouteUpdate (to, from, next) {
    this.queryParams = { ...to.query }
    this.handleMultiValuatedQueryParam(['authorizations'])
    Promise.resolve(this.getUses()).finally(() => next())
  },

  computed: {
    isProductPhyto () {
      return this.product.type === 'Phytopharmaceutique'
    },
    stades () {
      return this.$store.state.stades.stades
    }
  },

  methods: {
    init () {
      this.putQueryParamsInSearchInput()
      this.getUses()
    },

    getUses () {
      return Product.uses(this.productId, this.queryParams).then((response) => {
        const improvedUses = this.improveUses(response)
        const mergedUses = mergeSimilarUses(improvedUses)
        const sortedUses = sortUses(mergedUses)
        const groups = groupUses(sortedUses)
        this.usesGroups = groups
      })
    },

    filter (event) {
      if (!this.suggesterState || event.type === 'click') {
        this.putSearchInputInQueryParams()
        this.updateUrl()
      }
    },

    clearSearch () {
      for (const prop in this.searchInput) {
        prop === 'authorizations'
          ? (this.searchInput[prop] = ['authorized'])
          : (this.searchInput[prop] = '')
      }
      this.putSearchInputInQueryParams()
      this.updateUrl()
    },

    improveUses (uses) {
      return uses.map((use) => {
        const firstTerm = use.termsofuses_set[0]
        firstTerm.stade_bbch_start_label = this.buildStadeLabel(
          firstTerm.stade_bbch_start
        )
        firstTerm.stade_bbch_end_label = this.buildStadeLabel(
          firstTerm.stade_bbch_end
        )
        delete firstTerm.stade_bbch_start
        delete firstTerm.stade_bbch_end
        return use
      })
    },

    buildStadeLabel (stadeCode) {
      const stade = this.stades[stadeCode]
      return stade ? `${stade.stade} (${stade.name})` : ''
    }
  }
}

function groupUses (uses = []) {
  const groupsObject = _groupBy(uses, getUseGroupingKey)
  const groups = Object.values(groupsObject).map(buildUsesGroup)
  return groups
}

function buildUsesGroup (uses) {
  const firstUse = uses[0]
  const group = {
    key: getUseGroupingKey(firstUse),
    name: firstUse.name,
    code: firstUse.code,
    status: firstUse.status,
    approval_date_start: firstUse.approval_date_start,
    approval_date_end: firstUse.approval_date_end,
    uses
  }
  return group
}

/**
 * Merge uses having same data in properties described in getUseMergingKey
 */
function mergeSimilarUses (uses = []) {
  const groupObject = _groupBy(uses, getUseMergingKey)
  const mergedUses = Object.values(groupObject).map(mergeUses)
  return mergedUses
}

/**
 * Merge uses, and concat cultivations & targets
 */
function mergeUses (uses = []) {
  const use = uses[0]
  const allCultivations = uses.map((x) => x.cultivations).flat()
  const allTargets = uses.map((x) => x.targets).flat()
  use.cultivations = _sortBy([...new Set(allCultivations)]) // remove duplicates and sort
  use.targets = _sortBy([...new Set(allTargets)])
  return use
}

/**
 * Sort by name, then by cultivations (if names are same)
 */
function sortUses (uses = []) {
  const sortedUses = [...uses]
  sortedUses.sort(
    (a, b) =>
      a.name?.localeCompare(b.name) ||
      a.cultivations?.join(',')?.localeCompare(b.cultivations?.join(','))
  )
  return sortedUses
}

function getUseGroupingKey (use) {
  return `${use.code}-${use.name}`
}

function getUseMergingKey (use) {
  return JSON.stringify({
    name: use.name,
    code: use.code,
    treatments: use.treatments,
    restrictions: use.restrictions,
    ephy_comment: use.ephy_comment,
    ...use.termsofuses_set
  })
}
</script>
